import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from 'react'
import type { UiState } from 'instantsearch.js'
import { useInstantSearch, useMenu, useRange } from 'react-instantsearch'
import { setQueryParams } from '../../../../assets/frontend/js/utils/url'
import {
  getDateFilters,
  nowFloorHour,
  type TDateFilter
} from '../../../../assets/constants/datetime'
import { getQueryParams } from '../../../../assets/frontend/js/utils/url'
import {
  useConfigContext,
  type IndexName
} from './ConfigContext'

export type TDateFilterDirection = 'past' | undefined
export type TDateFilterFrame =
  | {
      id: TDateFilter
      range: [number | undefined, number | undefined]
    }
  | undefined

export type TFilterContext = {
  refineDirection: (value: TDateFilterDirection) => void
  refineFrame: (value: TDateFilterFrame) => void
  refineType: (value: TTypeFilterValue) => void
  type: TTypeFilterValue
  reset: () => void
  frame: TDateFilterFrame
  direction: TDateFilterDirection
}

export type TTypeFilterValue = 'offers' | 'events' | undefined

const filterContextInitial: TFilterContext = {
  refineDirection: () => {},
  refineFrame: () => {},
  refineType: () => {},
  frame: undefined,
  direction: undefined,
  reset: () => {},
  type: undefined
}


const FiltersContext = createContext<TFilterContext>(filterContextInitial)

function getTimeFrameById(id: TDateFilter) {
  if (!id) return undefined
  const dateFilters = getDateFilters(id)
  if (!dateFilters) return undefined
  return { id, range: dateFilters.value }
}

function getStartDateRefineValue(
  timeFrame: TDateFilterFrame
) {
  return [undefined, timeFrame?.range[1] ?? undefined]
}

function getEndDateRefineValue(
  timeFrame: TDateFilterFrame,
  direction: TDateFilterDirection
) {
  const now = nowFloorHour()
  let refinement: [number | undefined, number | undefined]
  switch (direction) {
    case 'past':
      refinement = [undefined, Math.min(now, timeFrame?.range[0] ?? now)]
      break
    default:
      refinement = [Math.max(now, timeFrame?.range[0] ?? now), undefined]
  }
  return refinement
}

function getTypeFromUiState({
  uiState,
  indexName
}: {
  uiState: UiState
  indexName: IndexName
}): TTypeFilterValue {
  const indexState = uiState[indexName]
  const value = indexState?.menu?.isPermanent
  return value === 'true' ? 'offers' : value === 'false' ? 'events' : undefined
}

type EventsFiltersControllerProps = {
  children: ReactNode
  startAttribute: string
  endAttribute: string
}


export function EventsFiltersController({
  children,
  startAttribute,
  endAttribute
}: EventsFiltersControllerProps) {

  const { indexName, typePreFilter } = useConfigContext()
  const { uiState, setIndexUiState } = useInstantSearch()

  const queryParams = useRef(getQueryParams())

  const timeFrameRef = useRef<TDateFilterFrame>(
    getTimeFrameById(queryParams.current.timeFrame as TDateFilter)
  )
  const timeDirectionRef = useRef<TDateFilterDirection>(
    queryParams.current.showPast === 'true' ? 'past' : undefined
  )

  const [timeFrame, setTimeFrame] = useState<TDateFilterFrame>(
    timeFrameRef.current
  )
  const [timeDirection, setTimeDirection] = useState<TDateFilterDirection>(
    timeDirectionRef.current
  )

  const typeFilterRef = useRef<TTypeFilterValue>(undefined)
  const [selectedType, setSelectedType] = useState<TTypeFilterValue>(undefined)
  
  useRange({ attribute: startAttribute }) // only for ui update
  useRange({ attribute: endAttribute }) // only for ui update
  useMenu({ attribute: 'isPermanent' }) // only for ui update

  const isInitialTypeSet = useRef(false)

  useEffect(() => {
    if (typePreFilter === typeFilterRef.current && isInitialTypeSet.current) return;
    if (!isInitialTypeSet.current) {
      isInitialTypeSet.current = true
      updateValues({ type: typePreFilter || getTypeFromUiState({ uiState, indexName }) })
      return
    }
    updateValues({ type: typePreFilter })
  }, [typePreFilter])


  const updateValues = useCallback((values?: { timeFrame?: TDateFilterFrame, timeDirection?: TDateFilterDirection, type?: TTypeFilterValue }) => {

    if (values) {
      if ('type' in values) {
        typeFilterRef.current = values.type
      }

      if ('timeDirection' in values) {
        timeDirectionRef.current = values.timeDirection
        if (timeDirectionRef.current !== undefined) {
          timeFrameRef.current = undefined
        }
      }

      if ('timeFrame' in values) {
        timeFrameRef.current = values.timeFrame
        if (timeFrameRef.current !== undefined) {
          timeDirectionRef.current = undefined
        }
      }
    }

    if (typeFilterRef.current === 'offers') {
      timeFrameRef.current = undefined
      timeDirectionRef.current = undefined
    }
    
    setTimeFrame(timeFrameRef.current)
    setTimeDirection(timeDirectionRef.current)

    if (values && 'type' in values) {
      setSelectedType(values.type)
    }

    refine()

    setQueryParams({
      timeFrame: timeFrameRef.current?.id,
      showPast: timeDirectionRef.current === 'past' || undefined
    })
  }, [])

  const refine = useCallback(() => {
    setIndexUiState((state) => {

      const startDateRefineValue = getStartDateRefineValue(timeFrameRef.current)
      const endDateRefineValue = getEndDateRefineValue(timeFrameRef.current, timeDirectionRef.current)

      const rangeFilterStartValue = startDateRefineValue.some(Boolean) && startDateRefineValue.map(v => v ?? '').join(':')
      const rangeFilterEndValue = endDateRefineValue.some(Boolean) && endDateRefineValue.map(v => v ?? '').join(':')

      const newRange = { ...(state.range ?? {}), [startAttribute]: rangeFilterStartValue, [endAttribute]: rangeFilterEndValue }

      if (newRange[startAttribute] === undefined) {
        delete newRange[startAttribute]
      }
      if (newRange[endAttribute] === undefined) {
        delete newRange[endAttribute]
      }

      const isPermanentValue = typeFilterRef.current === 'offers' ? 'true' : typeFilterRef.current === 'events' ? 'false' : ''

      const newMenu: Record<string, string> = { 
        ...(state.menu ?? {}),
        isPermanent: isPermanentValue
      }

      if (newMenu.isPermanent === '') {
        delete newMenu.isPermanent
      }

      // note: we have to reset to page 1 to prevent no results after "load more" button has been clicked
      return { ...state, page: 1, menu: newMenu, range: newRange } as UiState
    })
 
  }, [setIndexUiState, indexName, startAttribute, endAttribute])

  const onTimeDirectionChange = useCallback((value: TDateFilterDirection) => updateValues({ timeDirection: value }), [updateValues])
  const onTimeFrameChange = useCallback((value: TDateFilterFrame) => updateValues({ timeFrame: value }), [updateValues])
  const onTypeChange = useCallback((value: TTypeFilterValue) => updateValues({ type: value }), [updateValues])

  const resetDates = useCallback(() => {
    updateValues({ timeFrame: undefined, timeDirection: undefined })
  }, [])

  return (
    <FiltersContext.Provider
      value={{
        refineDirection: onTimeDirectionChange,
        refineFrame: onTimeFrameChange,
        refineType: onTypeChange,
        frame: timeFrame,
        direction: timeDirection,
        type: selectedType,
        reset: resetDates
      }}
    >
        {children}
    </FiltersContext.Provider>
  )
}

export const useFilters = () => useContext(FiltersContext)
